import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RawMath } from "@/components/math-text";
import type { EquationBuilderContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** equation_builder — mirror of iOS EquationBuilderView. Split the LaTeX
 *  `equation_template` on `___` into text segments + inline blank chips; the
 *  learner taps a pool tile to fill the active (or next-empty) blank. Tiles are
 *  reusable; tapping a filled blank clears it. All-or-nothing grading. */
export function EquationBuilderRenderer({
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as EquationBuilderContent;
  const parts = useMemo(() => (c.equation_template ?? "").split("___"), [c.equation_template]);
  const slotCount = Math.max(0, parts.length - 1);
  const pool = useMemo(() => shuffled(c.pool ?? []), [c.pool]);
  const [selected, setSelected] = useState<number | null>(null);

  const picks: string[] = useMemo(() => {
    const arr = (answer as string[]) ?? [];
    return Array.from({ length: slotCount }, (_, i) => arr[i] ?? "");
  }, [answer, slotCount]);

  const allFilled = slotCount > 0 && picks.every((p) => p !== "");

  const setAt = (i: number, val: string) => {
    const next = [...picks];
    next[i] = val;
    setAnswer(next);
  };

  const tapBlank = (i: number) => {
    if (revealed) return;
    if (picks[i] !== "") {
      setAt(i, "");
      setSelected(i);
      return;
    }
    setSelected(selected === i ? null : i);
  };

  const tapTile = (tile: string) => {
    if (revealed) return;
    const target = selected ?? picks.findIndex((p) => p === "");
    if (target < 0 || target >= slotCount) return;
    setAt(target, tile);
    setSelected(null);
  };

  const blankCorrect = (i: number): boolean =>
    (picks[i] ?? "").trim().toLowerCase() === (c.correct?.[i] ?? "").trim().toLowerCase();

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>

      {/* Equation skeleton — text segments interleaved with blank chips. */}
      <div className="mt-4 flex flex-wrap items-center gap-1.5 rounded-xl bg-black/[0.03] p-3.5 text-base">
        {parts.map((part, i) => {
          const chosen = picks[i] ?? "";
          const ok = revealed ? blankCorrect(i) : null;
          return (
            <span key={i} className="flex flex-wrap items-center gap-1.5">
              {part && <RawMath text={part} />}
              {i < slotCount && (
                <button
                  type="button"
                  disabled={revealed}
                  onClick={() => tapBlank(i)}
                  className={`inline-flex min-w-[2.25rem] items-center justify-center rounded-md border px-2.5 py-1 text-sm transition ${
                    revealed
                      ? ok
                        ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                        : "border-red-400 bg-red-50 text-red-700"
                      : selected === i
                        ? "border-brand-500 bg-brand-100 ring-1 ring-brand-300"
                        : chosen
                          ? "border-brand-400 bg-brand-50 text-brand-800"
                          : "border-dashed border-black/25 bg-white text-ink-faint"
                  }`}
                >
                  {chosen ? <RawMath text={chosen} /> : "?"}
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* Pool of reusable tiles. */}
      {!revealed && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-ink-faint">
            {selected !== null
              ? t("Tap a part to fill the highlighted blank")
              : t("Tap a part — it fills the next empty blank")}
          </p>
          <div className="flex flex-wrap gap-2">
            {pool.map((tile, k) => (
              <button
                key={k}
                type="button"
                disabled={allFilled}
                onClick={() => tapTile(tile)}
                className="rounded-lg border border-brand-300/60 bg-white px-3.5 py-2 text-sm transition hover:bg-brand-50 disabled:opacity-40"
              >
                <RawMath text={tile} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reveal: show the correct tile beside any wrong blank. */}
      {revealed &&
        picks.some((_, i) => !blankCorrect(i)) && (
          <div className="mt-3 space-y-1 text-xs text-emerald-700">
            {picks.map((_, i) =>
              blankCorrect(i) ? null : (
                <p key={i}>
                  {t("Blank {{n}}", { n: i + 1 })}: {t("Answer:")}{" "}
                  <span className="font-semibold">
                    <RawMath text={c.correct?.[i] ?? ""} />
                  </span>
                </p>
              ),
            )}
          </div>
        )}

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
